import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import './ActiveRides.css';

function rideToVehicle(ride) {
  const statusMap = {
    checked_in: 'parked',
    intent_pickup: 'being retrieved',
    held: 'being parked',
    finished: 'checked out',
  };
  return {
    id: ride.id,
    model: ride.car_details ?? '—',
    plate: ride.card_id ?? '—',
    location: ride.parking_space ?? '—',
    status: statusMap[ride.status] ?? ride.status,
    parkedBy: ride.valet_name ?? ride.valet_id ?? '—',
  };
}

export default function Vehicles() {
  const [vehicles, setVehicles] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all'); // 'all' | 'active'
  const [loading, setLoading] = useState(false);
  const [valets, setValets] = useState([]);
  const [showCheckIn, setShowCheckIn] = useState(false);
  const [checkInForm, setCheckInForm] = useState({
    card_id: '',
    valet_id: '',
    parking_space: '',
    car_details: '',
  });
  const [showRetrieve, setShowRetrieve] = useState(false);
  const [retrieval, setRetrieval] = useState({ rideId: null, pickup_valet_id: '' });
  const navigate = useNavigate();

  useEffect(() => {
    async function fetchRides() {
      setLoading(true);
      try {
        const { rides } = await api.listRides();
        setVehicles(rides.map(rideToVehicle));
      } catch (err) {
        console.error('Could not load rides', err);
      } finally {
        setLoading(false);
      }
    }
    fetchRides();
  }, []);

  useEffect(() => {
    async function fetchValets() {
      try {
        const { valets } = await api.listValets();
        setValets(valets || []);
      } catch (err) {
        console.error('Could not load valets', err);
      }
    }
    fetchValets();
  }, []);

  const filteredVehicles = useMemo(
    () => vehicles.filter((v) => (filterStatus === 'all' ? true : v.status !== 'checked out')),
    [vehicles, filterStatus]
  );

  const counters = useMemo(() => {
    const countBy = (st) => vehicles.filter((v) => v.status === st).length;
    return {
      parked: countBy('parked'),
      parking: countBy('being parked'),
      retrieving: countBy('being retrieved'),
      checkedOut: countBy('checked out'),
    };
  }, [vehicles]);

  const handleRetrieveCar = useCallback((vehicleId) => {
    setRetrieval({ rideId: vehicleId, pickup_valet_id: '' });
    setShowRetrieve(true);
  }, []);

  const submitRetrieve = useCallback(
    async (e) => {
      e.preventDefault();
      if (!retrieval.rideId || !retrieval.pickup_valet_id) return;

      setVehicles((prev) =>
        prev.map((v) => (v.id === retrieval.rideId ? { ...v, status: 'being retrieved' } : v))
      );

      try {
        await api.markIntentPickup(retrieval.rideId, {
          pickup_valet_id: Number(retrieval.pickup_valet_id),
        });
        setShowRetrieve(false);
        setRetrieval({ rideId: null, pickup_valet_id: '' });
        const { rides } = await api.listRides();
        setVehicles(rides.map(rideToVehicle));
      } catch (err) {
        console.error('Failed to mark ride as intent_pickup', err);
        alert('Failed to start retrieval.');
      }
    },
    [retrieval]
  );

  const handleFinishCar = useCallback(async (vehicleId) => {
    setVehicles((prev) => prev.map((v) => (v.id === vehicleId ? { ...v, status: 'checked out' } : v)));
    try {
      await api.finishRide(vehicleId);
      const { rides } = await api.listRides();
      setVehicles(rides.map(rideToVehicle));
    } catch (err) {
      console.error('Failed to finish ride', err);
      alert('Failed to complete the ride.');
    }
  }, []);

  const handleCheckIn = () => setShowCheckIn(true);

  const submitCheckIn = useCallback(
    async (e) => {
      e.preventDefault();
      try {
        await api.createRide({
          card_id: checkInForm.card_id,
          valet_id: Number(checkInForm.valet_id),
          parking_space: checkInForm.parking_space || undefined,
          car_details: checkInForm.car_details || undefined,
        });
        setShowCheckIn(false);
        setCheckInForm({ card_id: '', valet_id: '', parking_space: '', car_details: '' });
        const { rides } = await api.listRides();
        setVehicles(rides.map(rideToVehicle));
      } catch (err) {
        console.error('Failed to create ride', err);
        alert('Failed to check in car.');
      }
    },
    [checkInForm]
  );

  const parkingSlots = [
    { id: 1, label: 'A1', location: 'Lot 107' },
    { id: 2, label: 'A2', location: 'Lot 108' },
    { id: 3, label: 'A3', location: 'Lot 109' },
    { id: 4, label: 'B1', location: 'Lot 115' },
    { id: 5, label: 'B2', location: 'Lot 112' },
    { id: 6, label: 'B3', location: 'Lot 120' },
    { id: 7, label: 'C1', location: 'Lot 110' },
    { id: 8, label: 'C2', location: 'Lot 111' },
  ];

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h2 className="page-title">Vehicles</h2>
          <p className="page-sub">Live fleet overview & slot status</p>
        </div>
        <button className="btn btn-primary" onClick={handleCheckIn}>Check In Car</button>
      </div>

      <div className="kpis card">
        <div className="kpi"><span className="kpi-label">Parked</span><span className="kpi-value">{counters.parked}</span></div>
        <div className="kpi"><span className="kpi-label">Being Parked</span><span className="kpi-value">{counters.parking}</span></div>
        <div className="kpi"><span className="kpi-label">Being Retrieved</span><span className="kpi-value">{counters.retrieving}</span></div>
        <div className="kpi"><span className="kpi-label">Checked Out</span><span className="kpi-value">{counters.checkedOut}</span></div>
        <div className="kpi-controls">
          <div className="btn-group">
            <button className={`btn ${filterStatus==='all' ? 'btn-chip-active':'btn-chip'}`} onClick={() => setFilterStatus('all')}>
              Show All
            </button>
            <button className={`btn ${filterStatus==='active' ? 'btn-chip-active':'btn-chip'}`} onClick={() => setFilterStatus('active')}>
              Exclude Checked Out
            </button>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h3>{loading ? 'Loading…' : 'All Vehicles'}</h3>
        </div>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Model</th>
                <th>Plate</th>
                <th>Location</th>
                <th>Status</th>
                <th>Parked By</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredVehicles.map((v) => (
                <tr key={v.id}>
                  <td>{v.model}</td>
                  <td>{v.plate}</td>
                  <td>{v.location}</td>
                  <td>
                    <span
                      className={`chip ${
                        v.status === 'parked'
                          ? 'chip-ok'
                          : v.status === 'being retrieved'
                          ? 'chip-info'
                          : v.status === 'being parked'
                          ? 'chip-warn'
                          : 'chip-muted'
                      }`}
                    >
                      {v.status}
                    </span>
                  </td>
                  <td>{v.parkedBy}</td>
                  <td className="row-actions">
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => handleRetrieveCar(v.id)}
                      disabled={v.status !== 'parked'}
                      aria-disabled={v.status !== 'parked'}
                    >
                      Retrieve
                    </button>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => handleFinishCar(v.id)}
                      disabled={v.status !== 'being retrieved'}
                    >
                      Complete
                    </button>
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => navigate(`/ride/${v.id}`)}
                    >
                      Details
                    </button>
                  </td>
                </tr>
              ))}
              {filteredVehicles.length === 0 && !loading && (
                <tr><td colSpan="6" className="empty">No vehicles to display.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="slots">
        <h3 className="section-title">Parking Structure</h3>
        <div className="grid">
          {parkingSlots.map((slot) => {
            const occupied = vehicles.find(
              (v) => v.location === slot.location && v.status !== 'checked out'
            );
            return (
              <div key={slot.id} className={`slot card ${occupied ? 'slot-occupied' : ''}`}>
                <h4 className="slot-title">{slot.label}</h4>
                <p className="slot-sub">Location: {slot.location}</p>
                {occupied ? (
                  <>
                    <p className="slot-meta">{occupied.model}</p>
                    <p className="slot-meta">{occupied.plate}</p>
                  </>
                ) : (
                  <p className="slot-empty">Empty</p>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Check-In Modal */}
      {showCheckIn && (
        <div className="modal-backdrop" role="dialog" aria-modal="true">
          <div className="modal card">
            <div className="modal-head">
              <h3>Check In Car</h3>
            </div>
            <form onSubmit={submitCheckIn} className="form">
              <label className="field">
                <span>Card ID</span>
                <input
                  value={checkInForm.card_id}
                  onChange={(e) => setCheckInForm((f) => ({ ...f, card_id: e.target.value }))}
                  required
                />
              </label>

              <label className="field">
                <span>Valet</span>
                <select
                  value={checkInForm.valet_id}
                  onChange={(e) => setCheckInForm((f) => ({ ...f, valet_id: e.target.value }))}
                  required
                >
                  <option value="" disabled>Select a valet</option>
                  {valets.map((v) => (
                    <option key={v.id} value={v.id}>{v.name ?? `Valet #${v.id}`}</option>
                  ))}
                </select>
              </label>

              <label className="field">
                <span>Parking Space (optional)</span>
                <input
                  value={checkInForm.parking_space}
                  onChange={(e) => setCheckInForm((f) => ({ ...f, parking_space: e.target.value }))}
                />
              </label>

              <label className="field">
                <span>Car Details (optional)</span>
                <input
                  value={checkInForm.car_details}
                  onChange={(e) => setCheckInForm((f) => ({ ...f, car_details: e.target.value }))}
                />
              </label>

              <div className="actions">
                <button type="button" className="btn btn-ghost" onClick={() => setShowCheckIn(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">Check In</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Retrieve Modal */}
      {showRetrieve && (
        <div className="modal-backdrop" role="dialog" aria-modal="true">
          <div className="modal card">
            <div className="modal-head">
              <h3>Start Retrieval</h3>
            </div>
            <form onSubmit={submitRetrieve} className="form">
              <label className="field">
                <span>Pickup Valet</span>
                <select
                  value={retrieval.pickup_valet_id}
                  onChange={(e) => setRetrieval((r) => ({ ...r, pickup_valet_id: e.target.value }))}
                  required
                >
                  <option value="" disabled>Select a valet</option>
                  {valets.map((v) => (
                    <option key={v.id} value={v.id}>{v.name ?? `Valet #${v.id}`}</option>
                  ))}
                </select>
              </label>

              <div className="actions">
                <button type="button" className="btn btn-ghost" onClick={() => setShowRetrieve(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={!retrieval.pickup_valet_id}>
                  Retrieve
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
